import { useState } from 'react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Note } from '../../components/ui/note'
import { Step, Steps } from '../../components/ui/steps'

/** Connect Figma with a personal access token — the only way in on a server
 *  without an approved Figma app, and the fallback (behind a link) on one with. */
export function FigmaTokenForm({
  busy,
  collapsed,
  openLabel = 'or use an access token',
  onSubmit,
}: {
  busy: boolean
  collapsed: boolean
  openLabel?: string
  /** resolves to the reason when Figma turns the token down */
  onSubmit: (token: string) => Promise<string | null>
}) {
  const [open, setOpen] = useState(!collapsed)
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (busy || !token.trim()) return
    const reason = await onSubmit(token.trim())
    setError(reason)
    if (reason) return
    setToken('')
    setOpen(!collapsed)
  }

  if (!open) {
    return (
      <Button variant="bare" size="inline" className="self-start text-ink-faint" onClick={() => setOpen(true)}>
        {openLabel}
      </Button>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <Steps>
        <Step>
          In Figma, open{' '}
          <a
            className="underline underline-offset-2 hover:text-ink"
            href="https://www.figma.com/settings"
            target="_blank"
            rel="noreferrer"
          >
            Settings → Security
          </a>{' '}
          and choose Generate new token.
        </Step>
        <Step>
          Give it the <b className="font-semibold">File content: read</b> scope, and any expiry.
        </Step>
        <Step>Paste it here. When it expires, paste a new one.</Step>
      </Steps>
      <div className="flex gap-2">
        <Input
          type="password"
          autoComplete="off"
          variant="well"
          className="min-w-0 flex-1"
          placeholder="figd_…"
          aria-label="Figma personal access token"
          value={token}
          disabled={busy}
          onChange={(e) => {
            setToken(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => e.key === 'Enter' && void submit()}
        />
        <Button variant="default" size="sm" disabled={busy || !token.trim()} onClick={submit}>
          {busy ? 'Checking…' : 'Connect'}
        </Button>
      </div>
      <Note>It stays on the server, and Doop only uses it to read the files you import.</Note>
      {error && <Note tone="error">{error}</Note>}
    </div>
  )
}
